import { Subject, BehaviorSubject, defer, combineLatest, NEVER, of } from 'rxjs'
import { first, map, tap, catchError, debounceTime, switchMap, distinctUntilChanged, startWith, scan, concatMap, filter } from 'rxjs/operators'
import { includes, intersection, isEqual, mapValues, omitBy, pick, reduce, result } from 'libraries/lodash'
import { toAssociatedResourceAnnotation } from 'mapping/post'
import { UserUrn, User } from 'services/SessionService'
import { QUALIFIER_COMMENTS, entry, query, upsert } from 'services/AssociatedResource'
import { requestDecrypted, requestEncrypted } from 'front-end-core/services/EncryptionService'
import { isValidResponse, mergeResponses } from 'front-end-core/helpers/dataflow'
import debug from 'front-end-core/core/debug'
import { RESPONSE_PENDING } from 'front-end-core/constants/responses'
import { USER_ANNOTATIONS_PUBLISHER, USER_ANNOTATIONS_TECH } from 'constants/user-roles'
import reduxStore from 'redux/store'
import { setAnnotationsCommentsIsFiltered } from 'redux/actions/annotations'

const userRole = function ({ include = [], exclude = [] }) {
  return User.pipe(
    first(),
    map(x => x.roles),
    map(roles => {
      if (
        !reduce(include, (res, role) => res && includes(roles, role), true) ||
        !reduce(exclude, (res, role) => res && !includes(roles, role), true)
      ) {
        throw new Error('Insufficient privileges')
      }

      return true
    })
  )
}

const AdminTech = defer(() => userRole({ include: [USER_ANNOTATIONS_TECH] })).pipe(
  tap(isAllowed => {
    if (!isAllowed) {
      throw new Error('Insufficient privileges')
    }
  })
)

const makeRequest = (target) => {
  const reduxState = reduxStore.getState()
  const { user: { details: userDetails }, aol: { availableAol } } = reduxState

  const filter = ({ partyUrn, privacy }) => (
    // users own comments
    partyUrn === userDetails.uri ||
    // public comments
    privacy === 'public' ||
    // admin sees all
    includes(userDetails.roles, USER_ANNOTATIONS_TECH)
  )

  for (let aolIndex = 0; aolIndex < availableAol.length; aolIndex++) {
    const currentAol = availableAol[aolIndex].urlFragment
    if (currentAol.length) {
      const observables = {
        mergeResponses: [...mergeResponses],
        query: query(QUALIFIER_COMMENTS, `urn:esv:article:${currentAol}:${target}`, { partyJoin: false, _: Date.now() })
      }
      combineLatest(observables).pipe(
        catchError(e => debug.error(e) || NEVER),
        map(response => Comments.filterResponse(filter, response))
      )
        .subscribe(response => {
          response.roles && reduxStore.dispatch(setAnnotationsCommentsIsFiltered(true))
          return comments.mergeIn(response)
        })
    }
  }

  return comments
}

const mergeEntry = (next, current = null, decryptList) => {
  const reduxState = reduxStore.getState()
  const userDetails = reduxState.user.details

  if (!current) {
    decryptList.push(next.id)
  }

  const comment = Object.assign(
    { body: null, title: null, decryptionStatus: 'pending', canEdit: false, canArchive: false, canChangePrivacy: false },
    current || {},
    next
  )

  if (userDetails) {
    const { uri, roles } = userDetails
    const { active, partyUrn, privacy } = comment

    Object.assign(
      comment,
      {
        canEdit: active && (
          // allowed to owners, publishers (only public comments) and admin (tech role)
          uri === partyUrn ||
          (privacy === 'public' && includes(roles, USER_ANNOTATIONS_PUBLISHER)) ||
          includes(roles, USER_ANNOTATIONS_TECH)
        ),
        canArchive:
          // comments can be deleted by their owners and admin (tech role)
          uri === partyUrn ||
          (privacy === 'public' && includes(roles, USER_ANNOTATIONS_PUBLISHER)) ||
          includes(roles, USER_ANNOTATIONS_TECH),
        canChangePrivacy:
          // only publishers are allowed to change privacy and only on their own comments
          uri === partyUrn && includes(roles, USER_ANNOTATIONS_PUBLISHER)
      }
    )
  }
  return comment
}

class Comments extends BehaviorSubject {
  constructor () {
    super({
      references: {},
      entries: {}
    })

    const targetRequests = new Subject()

    Object.assign(this, {
      requestTarget: (target) => targetRequests.next(target)
    })

    targetRequests.pipe(debounceTime(250))
      .subscribe(makeRequest)
  }

  mergeIn = (response) => {
    const state = this.value
    const decryptList = []

    if (isValidResponse(response)) {
      const references = { ...state.references, ...response.references }
      const allIds = reduce(references, (a, b) => [...a, ...b], [])
      const entries = pick(
        Object.assign(
          {},
          state.entries,
          ...Object.keys(response.entries)
            .map(id => ({
              [id]: mergeEntry(response.entries[id], state.entries[id], decryptList)
            }))
        ),
        allIds
      )
      this.next({ entries, references })
    }

    decryptList.forEach(this.decryptWatch)
  }

  updateEntry = (id, entry) => {
    Object.prototype.hasOwnProperty.call(this.value.entries, id) &&
    this.mergeIn({
      entries: {
        [id]: entry
      }
    })
  }

  decryptWatch = (id) => {
    this.pipe(
      map(x => x.entries.filter(entry => entry.id === id).filter(entry => entry.encrypted)), // DEBUG AND FIX ME
      distinctUntilChanged(),
      switchMap(Comments.requestDecrypted)
    ).subscribe(result => this.mergeIn({ entries: { [id]: result } }))
  }

  static requestDecrypted (encrypted) {
    return requestDecrypted(encrypted).pipe(map(res => res !== null
      ? { title: unescape(res.title), body: unescape(res.body), keyUrl: res.keyUrl, decryptionStatus: 'ok' }
      : { decryptionStatus: 'failed' }
    ))
  }

  static requestEncrypted ({ title, body }) {
    return requestEncrypted({ title: escape(title), body: escape(body) })
  }

  static filterResponse (filter, response) {
    const entries = omitBy(
      response.entries,
      entry => !filter(entry)
    )
    const entryIds = Object.keys(entries)

    const allMatchedRefs = mapValues(response.references, es => intersection(es, entryIds))
    const references = omitBy(allMatchedRefs, es => es.length === 0)

    return { ...response, entries, references }
  }

  request = (target, reference) => {
    this.requestTarget(target)
    return this.pipe(
      map(
        state =>
          result(state, ['references', reference], [])
            .map(id => state.entries[id])
      ),
      startWith([]),
      distinctUntilChanged(isEqual)
    )
  }

  upsert = ({
    commentTarget,
    commentCategory,
    id,
    commentReference,
    title,
    body,
    privacy = 'private',
    active,
    aol
  }) => {
    if (![
      commentTarget,
      commentReference,
      commentCategory,
      id,
      aol
    ].some(param => param)
    ) {
      throw new Error('Unable to upsert, check properties')
    }

    return combineLatest([
      UserUrn,
      Comments.requestEncrypted({ title, body })
    ],
    (owner, body) =>
      toAssociatedResourceAnnotation({
        body,
        commentReference,
        commentTarget,
        commentCategory,
        id,
        owner,
        privacy,
        active,
        aol
      })
    ).pipe(
      switchMap(upsert.bind(null, QUALIFIER_COMMENTS)),
      tap(success => {
        if (success) {
          this.updateEntry(id, { title, body, privacy })
          this.requestTarget(commentTarget)
        }
      }),
      first()
    )
  }

  reencrypt = (commentIds) => {
    const total = commentIds.length
    return AdminTech.pipe(
      switchMap(() => of(...commentIds)),
      concatMap(this.reencryptComment),
      scan(
        ({ index, errors }, { success, error = null }) =>
          ({ index: index + 1, error, errors: errors + (+!success) }),
        { index: 0, errors: 0 }
      ),
      map(e => ({ ...e, total })),
      catchError(e => ({ index: total, total, errors: 1, error: e.message }))
    )
  }

  reencryptComment = (commentId) => {
    const comment = this.value.entries[commentId]
    return AdminTech.pipe(
      switchMap(() => entry(comment.uri)),
      filter(x => x !== RESPONSE_PENDING),
      switchMap(
        e =>
          Comments.requestDecrypted(e.body)
            .map(decrypted => ({ ...comment, ...e, ...decrypted }))
      ),
      first(),
      switchMap(comment => Comments.requestEncrypted(comment).map(encrypted => ({ ...comment, encrypted }))),
      switchMap(
        c => {
          const comment = upsert(
            QUALIFIER_COMMENTS,
            toAssociatedResourceAnnotation({
              body: c.encrypted,
              commentReference: c.reference,
              commentTarget: c.target,
              id: c.id,
              owner: c.partyUrn,
              privacy: c.privacy,
              active: +c.active
              // aol
            })
          ).mapTo(c)
          return comment
        }
      ),
      tap(c => this.updateEntry(c.id, c)),
      map(x => ({ success: true })),
      catchError(e => of({ success: false, error: e.message }))
    )
  }
}

const comments = new Comments()

const whenEnabled = (fn, defaultValue = NEVER) => (...args) => {
  const reduxState = reduxStore.getState()
  const encryptionKeysLoaded = reduxState.user.encryptionKeysLoaded

  return encryptionKeysLoaded ? fn(...args) : defaultValue
}

export const upsertComment = whenEnabled(comments.upsert)
export const reEncryptComments = whenEnabled(comments.reencrypt)
export const requestComments = whenEnabled(comments.request)

const categoryFilter = category => res => {
  const splitRef = res.reference.split(':')
  const isLegislation = splitRef.length > 1

  if (category === 'legislation') {
    return isLegislation
  }

  if (isLegislation) {
    return false
  }

  if (!res.category) {
    // when category is undefined we must assume true
    // requestCommentsReference will then take care of filtering by flavour selected
    return true
  }

  return res.category === category
}

const privacyFilter = privacy => res => privacy === 'all'
  ? true
  : res.privacy === privacy

const usersFilter = users => res => users === 'all'
  ? true
  : users.indexOf(res.partyUrn) >= 0

const archivedFilter = archived => res =>
  archived === 'only'
    ? !res.active
    : archived === 'include'
      ? true
      : res.active

export const requestFilteredComments = function ({
  category,
  users = 'all',
  privacy = 'private',
  archived = 'exclude'
}) {
  return requestAllComments().pipe(
    map(res => Comments.filterResponse(categoryFilter(category), res)),
    map(res => Comments.filterResponse(privacyFilter(privacy), res)),
    map(res => Comments.filterResponse(archivedFilter(archived), res)),
    map(res => Comments.filterResponse(usersFilter(users), res))
  )
}

export const requestAllComments = whenEnabled(() => makeRequest('*'))
